import {
  parseAppSumoPreviewParams,
  type AppSumoPreviewParams,
} from '@/lib/appsumo-upgrade';

/** The wizard's steps, in order. Starter skips `addons`. */
export const UPGRADE_STEPS = ['plan', 'addons', 'review'] as const;
export type UpgradeStep = (typeof UPGRADE_STEPS)[number];

/** The self-serve tiers the wizard can sell. Enterprise is "talk to sales". */
export type UpgradeTier = 'starter' | 'pro';
export type UpgradeInterval = 'month' | 'year';

/** Billing's ceiling on a v4 subscription's seat quantity. */
export const MAX_SEATS = 10_000;

/** Billing's ceiling on Pro's extra senders. */
export const MAX_SENDERS = 100;

/**
 * Comfortably above the largest monthly credit add-on (98,500 today); the
 * wizard checks a size against the catalog before using it.
 */
const MAX_ADDON_CREDITS = 1_000_000;

/**
 * `/upgrade`'s search params. Every field is optional and the wizard fills the
 * gaps from the catalog, so a deep link can say as much or as little as it
 * knows (`?plan=pro&source=sidebar`). The URL is the wizard's state: a reload,
 * a Back from Stripe or a shared link lands on the same selection. In DEV
 * builds it also keeps the AppSumo offer's preview params, so a review of the
 * member price survives the wizard's own URL updates.
 */
export interface UpgradeSearch extends AppSumoPreviewParams {
  step?: UpgradeStep;
  plan?: UpgradeTier;
  interval?: UpgradeInterval;
  /** Paid seats. Clamped by the wizard to at least the member count. */
  seats?: number;
  /** Pro's monthly credit add-on size; 0 (or absent) is none. */
  credits?: number;
  /** Pro's extra senders, 1 mailbox + 1 LinkedIn account each; 0 is none. */
  senders?: number;
  /** Where the user came from, for analytics (`sidebar`, `credits_menu`...). */
  source?: string;
}

/** The wizard's tier for a tier key or plan label ("pro", "Starter"); any
 *  other plan (Enterprise, Scale, a legacy name) is not one. */
export function toUpgradeTier(
  value: string | null | undefined
): UpgradeTier | undefined {
  const key = value?.trim().toLowerCase();
  return key === 'pro' || key === 'starter' ? key : undefined;
}

const wholeNumber = (value: unknown, max: number): number | undefined => {
  const n = typeof value === 'string' ? Number(value) : value;
  return typeof n === 'number' && Number.isInteger(n) && n > 0 && n <= max
    ? n
    : undefined;
};

/** An add-on's size: a whole number up to `max`, or an explicit 0 (none). */
const addonSize = (value: unknown, max: number): number | undefined =>
  value === 0 || value === '0' ? 0 : wholeNumber(value, max);

/**
 * `validateSearch` for `/upgrade`: unknown or malformed values read as unset.
 * Every key stays in the result (as `undefined` when invalid) because the
 * router merges the raw search under it, and a missing key would let the raw
 * value through.
 */
export function parseUpgradeSearch(
  search: Record<string, unknown>
): UpgradeSearch {
  const parsed: UpgradeSearch = {
    step: UPGRADE_STEPS.includes(search.step as UpgradeStep)
      ? (search.step as UpgradeStep)
      : undefined,
    plan: toUpgradeTier(
      typeof search.plan === 'string' ? search.plan : undefined
    ),
    interval:
      search.interval === 'month' || search.interval === 'year'
        ? search.interval
        : undefined,
    seats: wholeNumber(search.seats, MAX_SEATS),
    credits: addonSize(search.credits, MAX_ADDON_CREDITS),
    senders: addonSize(search.senders, MAX_SENDERS),
    source:
      typeof search.source === 'string' && search.source.length <= 64
        ? search.source
        : undefined,
  };
  return import.meta.env.DEV
    ? { ...parsed, ...parseAppSumoPreviewParams(search) }
    : parsed;
}

/** `/plans`' required-but-undefined search keys. */
export const PLANS_SEARCH = {
  status: undefined,
  credits_added: undefined,
  plan_name: undefined,
} as const;

/** Where an upgrade entry point sends someone. */
export interface UpgradeTarget {
  /** The plan the wizard opens on. */
  plan?: UpgradeTier;
  /** Analytics: the surface it's on. */
  source?: string;
}

/**
 * The wizard's URL for an upgrade entry point, for a full-page navigation.
 * Prefer `UpgradeLink` or `useOpenUpgrade` in React code.
 */
export function upgradeHref({ plan, source }: UpgradeTarget = {}): string {
  const params = new URLSearchParams();
  if (plan) params.set('plan', plan);
  if (source) params.set('source', source);
  const query = params.toString();
  return query ? `/upgrade?${query}` : '/upgrade';
}
